import { useQuery } from "@apollo/client";
import { AddIcon } from "@chakra-ui/icons";
import {
  TableContainer,
  Flex,
  Menu,
  MenuButton,
  Button,
  MenuList,
  MenuItem,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Stat,
  StatArrow,
  Text,
  Center,
  Spinner,
  useDisclosure,
  useMediaQuery,
  Box,
} from "@chakra-ui/react";
import { get, isEmpty, reverse } from "lodash";
import moment from "moment";
import React from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaSync } from "react-icons/fa";
import { MdAutoGraph } from "react-icons/md";
import { useLocation, useParams } from "react-router-dom";
import GraphModal from "../../../../components/modals/commodity/GraphModal";
import UpdateCommodity from "../../../../components/modals/commodity/update/UpdateCommodity";
import {
  GET_RETAIL_HISTORY_PUBLIC,
  GET_WHOLE_SALE_HISTORY_PUBLIC,
} from "../../../public/queries";
import { GET_RETAIL_HISTORY, GET_WHOLE_SALE_HISTORY } from "./queries";

type PriceHistoryProps = {
  id?: string;
  type: string;
};

const PriceHistory = (props: PriceHistoryProps) => {
  const [isLargerThan900] = useMediaQuery("(min-width: 900px)");

  const location = useLocation();
  const isPublic = location.pathname.split("/").includes("public");

  const id = get(props, "id", undefined);
  const type = get(props, "type");

  const retailQuery = isPublic ? GET_RETAIL_HISTORY_PUBLIC : GET_RETAIL_HISTORY;
  const wholeSaleQuery = isPublic
    ? GET_WHOLE_SALE_HISTORY_PUBLIC
    : GET_WHOLE_SALE_HISTORY;

  const { data, loading, refetch } = useQuery(
    type === "retail" ? retailQuery : wholeSaleQuery,
    {
      variables: {
        id,
        range: {},
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  let prices: any = [];

  if (type === "retail") {
    prices = get(
      data,
      isPublic ? "publicCommodity.retailPrices" : "commodity.retailPrices"
    );
  } else {
    prices = get(
      data,
      isPublic ? "publicCommodity.wholeSalePrices" : "commodity.wholeSalePrices"
    );
  }

  const {
    isOpen: isUpdateModalOpen,
    onOpen: onOpenUpdateModal,
    onClose: onCloseUpdateModal,
  } = useDisclosure();

  const {
    isOpen: isGraphModalOpen,
    onOpen: onOpenGraphModal,
    onClose: onCloseGraphModal,
  } = useDisclosure();

  return (
    <Box>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        p="2"
        borderWidth="thin"
      >
        <Text fontWeight="bold">
          {type === "retail"
            ? "Retail Price History"
            : "Whole Sale Price History"}
        </Text>
        <Menu size="md">
          <MenuButton size="sm" as={Button}>
            <BsThreeDotsVertical />
          </MenuButton>
          <MenuList>
            {!isPublic && (
              <MenuItem icon={<AddIcon />} onClick={onOpenUpdateModal}>
                Update Price
              </MenuItem>
            )}

            <MenuItem onClick={() => refetch()} icon={<FaSync />}>
              Refresh
            </MenuItem>

            <MenuItem onClick={onOpenGraphModal} icon={<MdAutoGraph />}>
              Show Graph
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      <TableContainer
        shadow="xs"
        p="3"
        maxH={isLargerThan900 ? "55vh" : "auto"}
        overflowY={isLargerThan900 ? "auto" : "visible"}
      >
        {loading ? (
          <Center>
            <Spinner />
          </Center>
        ) : (
          <>
            <Table mt="5">
              <Thead>
                <Tr>
                  <Th>Price</Th>
                  <Th>Date</Th>
                  {/* <Th>Notes</Th> */}
                </Tr>
              </Thead>

              <Tbody>
                {!isEmpty(prices) ? (
                  prices?.map((retail: any, index: number) => {
                    return (
                      <Tr
                        cursor="pointer"
                        _hover={{
                          bg: "gray.100",
                        }}
                        key={retail.id}
                      >
                        <Td fontWeight="bold">
                          <Stat>
                            {retail?.price}

                            {retail?.price > prices[index + 1]?.price ? (
                              <StatArrow mx="2" type="increase" />
                            ) : (
                              <StatArrow mx="2" type="decrease" />
                            )}
                          </Stat>
                        </Td>
                        <Td>{moment(retail.createdAt).format("lll")}</Td>
                        {/* <Td>{retail.notes}</Td> */}
                      </Tr>
                    );
                  })
                ) : (
                  <Center p="5">
                    <Text>No Price yet.</Text>
                  </Center>
                )}
              </Tbody>
            </Table>
          </>
        )}

        {id && !isPublic && (
          <UpdateCommodity
            id={id}
            isOpen={isUpdateModalOpen}
            onOpen={onOpenUpdateModal}
            onClose={onCloseUpdateModal}
            retailOnly={type === "retail"}
            wholeSaleOnly={type !== "retail"}
            refetch={refetch}
            priceOnly={true}
          />
        )}

        <GraphModal
          isOpen={isGraphModalOpen}
          onOpen={onOpenGraphModal}
          onClose={onCloseGraphModal}
          prices={prices}
          title={type === "retail" ? "Retail" : "Whole Sale"}
          isPublic={isPublic}
        />
      </TableContainer>
    </Box>
  );
};

export default PriceHistory;
